import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExportOutlined } from '@ant-design/icons';

import { Table, Space } from 'antd';

import useDatasource from '../../hooks/useDatasource';
import Column from '../../helpers/columns';
import {
  findAll,
  findAllByInspectorId,
  downloadAll,
  downloadAllByInspectorId,
} from '../../services/validators/inspector-transactions';
import PageTitle from '../PageTitle';
import ExportEntitiesButton from '../buttons/ExportEntitiesButton';

const ACTIONS_DICT = {
  content: [
    { id: 'TO_INSPECTION' },
    { id: 'TO_COMMERCIAL' },
    { id: 'IDENTIFYING_INSPECTOR' },
    { id: 'VERIFYING_CARD' },
  ],
  loading: false,
  error: false,
};

const InspectorTransactionsList = ({ inspectorId }) => {
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const fetcher = useCallback(
    (data) =>
      inspectorId === undefined
        ? findAll(data)
        : findAllByInspectorId(data, inspectorId),
    [inspectorId],
  );
  const downloadFetcher = useCallback(
    (data, spec) =>
      inspectorId === undefined
        ? downloadAll(data, spec)
        : downloadAllByInspectorId(inspectorId, data, spec),
    [inspectorId],
  );

  const { pagination, content, criterias, handleChange, sort } =
    useDatasource(fetcher);

  const columns = useMemo(
    () => [
      ...(inspectorId === undefined
        ? [
            Column.text(
              'inspectorId',
              t('components.inspectors.transactions.inspectorId'),
              {
                filter: true,
              },
            ),
            Column.text(
              'fullname',
              t('components.inspectors.transactions.fullname'),
              {
                filter: true,
              },
            ),
          ]
        : []),
      {
        title: '',
        key: 'actions',
        fixed: 'right',
        align: 'center',
        render: (_, record) => {
          const lat = record?.latitude;
          const lng = record?.longitude;
          const canOpen =
            lat != null &&
            lng != null &&
            !Number.isNaN(+lat) &&
            !Number.isNaN(+lng);

          const openMap = (e) => {
            e.stopPropagation();
            if (!canOpen) return;
            const url = `https://www.google.com/maps?q=${encodeURIComponent(
              lat,
            )},${encodeURIComponent(lng)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
          };

          return (
            <button
              type="button"
              onClick={openMap}
              disabled={!canOpen}
              title={canOpen ? 'Open location' : 'No coordinates'}
              style={{
                border: 'none',
                background: 'transparent',
                padding: 0,
                lineHeight: 1,
                cursor: canOpen ? 'pointer' : 'not-allowed',
              }}
            >
              <ExportOutlined
                style={{ fontSize: 16, color: canOpen ? '#1677ff' : '#999' }}
              />
            </button>
          );
        },
      },
      Column.text(
        'latitude',
        t('components.inspectors.transactions.latitude'),
        {
          filter: true,
        },
      ),
      Column.text(
        'longitude',
        t('components.inspectors.transactions.longitude'),
        {
          filter: true,
        },
      ),
      Column.text('userId', t('components.inspectors.transactions.userId'), {
        filter: true,
      }),
      Column.dictionary(
        'action',
        t('components.inspectors.transactions.action'),
        ACTIONS_DICT,
        {
          filter: true,
          dictLabel: (entry) =>
            t(`components.inspectors.transactions.actionsDict.${entry.id}`),
        },
      ),
      Column.text(
        'passengerCounter',
        t('components.inspectors.transactions.passengerCounter'),
        {
          filter: true,
        },
      ),
      {
        ...Column.date(
          'transactionTime',
          t('components.inspectors.transactions.transactionTime'),
          { filter: true },
        ),
        ellipsis: true,
      },
      Column.text(
        'vehicleId',
        t('components.inspectors.transactions.vehicleId'),
        {
          filter: true,
        },
      ),
      Column.text('routeId', t('components.inspectors.transactions.routeId'), {
        filter: true,
      }),
      Column.bool(
        'direction',
        t('components.inspectors.transactions.direction'),
        {
          filter: true,
          labels: [
            t('table.filter.all'),
            t('components.inspectors.transactions.directionsDict.true'),
            t('components.inspectors.transactions.directionsDict.false'),
            t('table.filter.unknown'),
          ],
        },
      ),
      Column.text(
        'station1Id',
        t('components.inspectors.transactions.station1Id'),
        {
          filter: true,
        },
      ),
      Column.text(
        'station2Id',
        t('components.inspectors.transactions.station2Id'),
        {
          filter: true,
        },
      ),
      Column.text('zoneId', t('components.inspectors.transactions.zoneId'), {
        filter: true,
      }),
      Column.text('pto', t('components.inspectors.transactions.pto'), {
        filter: true,
      }),
      Column.text(
        'validatorPosition',
        t('components.inspectors.transactions.validatorPosition'),
        {
          filter: true,
        },
      ),
      Column.text(
        'deviceSn',
        t('components.inspectors.transactions.deviceSn'),
        {
          filter: true,
        },
      ),
    ],
    [inspectorId, t],
  );

  const rowSelection = useMemo(
    () => ({
      hideSelectAll: true,
      onSelect: (record, selected, selectedRowsNow) => {
        const lat = record?.latitude;
        const lng = record?.longitude;
        if (
          selected &&
          (lng !== 0 || lat !== 0 || lat !== null || lng !== null) &&
          !Number.isNaN(+lat) &&
          !Number.isNaN(+lng)
        ) {
          const url = `https://www.google.com/maps?q=${encodeURIComponent(
            lat,
          )},${encodeURIComponent(lng)}`;
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        setSelectedRowKeys(selectedRowsNow.map((r) => r.id));
      },
      onChange: (keys) => setSelectedRowKeys(keys),
      selectedRowKeys,
    }),
    [selectedRowKeys],
  );

  return (
    <>
      <PageTitle>
        <Space>
          <ExportEntitiesButton
            columns={columns}
            data={{
              sort,
              criterias,
            }}
            downloadFiles={downloadFetcher}
          />
        </Space>
      </PageTitle>
      <Table
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        rowKey="id"
        columns={columns}
        // loading={loading}
        pagination={pagination}
        dataSource={content}
        onChange={handleChange}
        scroll={{ x: 700, y: 700 }}
        size="small"
      />
    </>
  );
};

export default InspectorTransactionsList;
